import { sendFunnelEvent, setFlowContext } from './campaignAttribution';
import { analyticsAllowed } from './analyticsConsent';

/**
 * Session flows: how a visit moves through the public pages (clicks, scroll depth, sections seen, form steps, media,
 * time active), so the funnel can be read as paths. Only with analytics consent, only on the PAGES below, and never any
 * text, values or file names: an element is described by its tag, role and position in the page (e.g. "div:2>button:0").
 * The server accepts the same events and props (web/backend/utils/marketing.py) and drops them after 30 days.
 */
const PAGES = new Set(['/', '/son1', '/library', '/signup', '/signin', '/checkout', '/free-presets', '/about', '/pricing',
  '/dashboard', '/verify-email', '/contact/support', '/privacy', '/terms', '/account', '/profile', '/feedback', '/success', '/cancel']);
const ELEMENTS = new Set(['a', 'button', 'input', 'textarea', 'select', 'summary', 'form', 'label']);
const ROLES = new Set(['button', 'link', 'tab', 'checkbox', 'switch', 'slider', 'menuitem']);
const FIELD_TYPES = new Set(['text', 'email', 'password', 'checkbox', 'radio', 'file', 'submit', 'number', 'range', 'search', 'tel', 'url']);
// Named controls (data-flow, or else data-testid) and landing sections (element ids) reported by name.
const CONTROLS = new Set(['mode-find', 'mode-make', 'hero-line-explorer', 'hero-line-son1', 'explorer-try', 'explorer-upload',
  'landing-search', 'landing-prompt', 'son1-generate', 'hero-trial']);
const SECTIONS = new Set(['hero', 'son1', 'free-presets', 'organize', 'try-son1', 'pricing', 'find', 'faq', 'features', 'how-it-works']);
const CONTROL_SELECTOR = 'a,button,input,textarea,select,summary,[role="button"],[role="tab"],[role="checkbox"],[role="switch"],[role="slider"],[role="menuitem"]';
const DAY_MS = 86400000;
const IDLE_MS = 60000; // time after the last interaction still counted as active

export function describe(node) {
  const el = node?.closest?.(CONTROL_SELECTOR) || node;
  const tag = el?.tagName?.toLowerCase();
  if (!tag) return {};
  const path = [];
  for (let n = el, i = 0; n && n !== document.body && i < 5; i += 1, n = n.parentElement) {
    path.unshift(`${n.tagName.toLowerCase()}:${Array.prototype.indexOf.call(n.parentElement?.children || [], n)}`);
  }
  const out = { element: ELEMENTS.has(tag) ? tag : 'other', target: path.join('>').slice(0, 180) };
  const role = el.getAttribute?.('role');
  if (ROLES.has(role)) out.role = role;
  if (tag === 'input') out.field_type = FIELD_TYPES.has(el.type) ? el.type : 'other';
  if (tag === 'textarea' || tag === 'select') out.field_type = tag;
  const control = el.getAttribute?.('data-flow') || el.getAttribute?.('data-testid');
  if (CONTROLS.has(control)) out.control = control;
  const section = el.closest?.('[id]')?.id;
  if (SECTIONS.has(section)) out.section = section;
  if (tag === 'a') {
    try {
      const url = new URL(el.getAttribute('href'), window.location.href);
      const same = url.origin === window.location.origin;
      out.destination = same ? (PAGES.has(url.pathname) ? url.pathname : 'other_page') : 'external';
      if (same && SECTIONS.has(url.hash.slice(1))) out.section = url.hash.slice(1);
    } catch { /* not a URL */ }
  }
  return out;
}

/** Starts the tracker; returns { navigate, stop }. navigate() is called on every route change. */
export function startSessionFlows() {
  if (typeof crypto?.randomUUID !== 'function') return { navigate: () => {}, stop: () => {} };
  let page; let viewId; let sequence = 0; let activeMs = 0;
  let lastTick = Date.now(); let lastInput = lastTick;
  let seenSections = new Set(); let scrollPercent = 0; let lastDepth = -1;
  let lastTarget = null; let clicks = []; let exited = false;
  const tabId = crypto.randomUUID();
  let visible = document.visibilityState !== 'hidden';
  setFlowContext(() => (viewId ? { page, tab_id: tabId, view_id: viewId, sequence: ++sequence, client_time_ms: Date.now() } : {}));

  const send = (event, props = {}) => { if (analyticsAllowed() && PAGES.has(window.location.pathname)) sendFunnelEvent(event, props); };
  const tick = () => {
    const now = Date.now();
    if (visible) activeMs += Math.max(0, Math.min(now - lastTick, lastInput + IDLE_MS - lastTick));
    lastTick = now;
    visible = document.visibilityState !== 'hidden';
  };
  const timing = () => ({ active_ms: Math.min(activeMs, DAY_MS), scroll_percent: scrollPercent });
  // Opens a new view when the path changed; reports sections that came into view. False when not tracking.
  const sync = () => {
    if (!analyticsAllowed() || !PAGES.has(window.location.pathname)) { page = undefined; viewId = undefined; activeMs = 0; return false; }
    if (page !== window.location.pathname || !viewId) {
      if (viewId) { tick(); send('page_exit', { ...timing(), reason: 'navigation' }); }
      page = window.location.pathname;
      viewId = crypto.randomUUID();
      activeMs = 0; seenSections = new Set(); scrollPercent = 0; lastDepth = -1;
      lastTick = Date.now(); lastInput = lastTick; exited = false;
      send('flow_start', { viewport_width: window.innerWidth, viewport_height: window.innerHeight });
    }
    SECTIONS.forEach((id) => {
      const r = document.getElementById(id)?.getBoundingClientRect();
      if (r && r.height > 0 && r.top < window.innerHeight - 80 && r.bottom > 80 && !seenSections.has(id)) {
        seenSections.add(id);
        send('section_view', { section: id });
      }
    });
    return true;
  };
  const interacted = () => { tick(); lastInput = Date.now(); };
  const onField = (e) => {
    if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName) || !sync()) return;
    interacted();
    send(e.type === 'focusin' ? 'field_focus' : e.type === 'change' ? 'field_change' : 'form_invalid', describe(e.target));
  };
  const onError = (e) => { if (sync()) send('client_error', { error_code: e.type === 'unhandledrejection' ? 'unhandled_rejection' : 'javascript_or_resource_error' }); };
  const onMedia = (e) => {
    if (!['VIDEO', 'AUDIO'].includes(e.target?.tagName) || !sync()) return;
    interacted();
    send('media_action', { action: e.type, surface: e.target.tagName.toLowerCase(), position_ms: Math.min(DAY_MS, Math.round((e.target.currentTime || 0) * 1000)) });
  };
  const listeners = [
    [document, 'play', onMedia], [document, 'pause', onMedia], [document, 'ended', onMedia],
    [document, 'click', (e) => {
      if (!sync()) return;
      interacted();
      const d = describe(e.target);
      const now = Date.now();
      clicks = clicks.filter((t) => now - t < 1000);
      clicks.push(now);
      send('ui_click', {
        ...d,
        x_percent: Math.max(0, Math.min(100, Math.round((100 * e.clientX) / (window.innerWidth || 1)))),
        y_percent: Math.max(0, Math.min(100, Math.round((100 * e.clientY) / (window.innerHeight || 1)))),
        action: clicks.length === 3 && lastTarget === d.target ? 'repeated_click' : 'click',
      });
      lastTarget = d.target;
    }],
    [document, 'scroll', () => {
      if (!sync()) return;
      interacted();
      const h = document.documentElement.scrollHeight;
      const pct = h > window.innerHeight ? Math.min(100, Math.round((100 * (window.scrollY + window.innerHeight)) / h)) : 100;
      scrollPercent = Math.max(scrollPercent, pct);
      const depth = 25 * Math.floor(scrollPercent / 25);
      if (depth > lastDepth) { lastDepth = depth; send('scroll_depth', { ...timing(), scroll_percent: depth }); }
    }],
    [document, 'focusin', onField], [document, 'change', onField], [document, 'invalid', onField],
    [document, 'submit', (e) => { if (sync()) { interacted(); send('form_submit', describe(e.target)); } }],
    [document, 'visibilitychange', () => { if (sync()) { tick(); send('page_visibility', { ...timing(), action: document.visibilityState === 'hidden' ? 'hidden' : 'visible' }); } }],
    [window, 'pagehide', () => { if (sync() && !exited) { tick(); send('page_exit', { ...timing(), reason: 'pagehide' }); exited = true; } }],
    [window, 'pageshow', () => { exited = false; lastTick = Date.now(); sync(); }],
    [window, 'error', onError], [window, 'unhandledrejection', onError],
    [window, 'sonikly-analytics-choice', sync], [window, 'sonikly-analytics-ready', sync],
  ];
  listeners.forEach(([target, type, fn]) => target.addEventListener(type, fn, true));
  const timer = setInterval(() => {
    if (!sync()) return;
    tick();
    if (document.visibilityState !== 'hidden' && Date.now() - lastInput < IDLE_MS) send('engagement', timing());
  }, 30000);
  sync();
  return {
    navigate: sync,
    stop: () => {
      setFlowContext(null);
      clearInterval(timer);
      listeners.forEach(([target, type, fn]) => target.removeEventListener(type, fn, true));
    },
  };
}
